<script setup lang="ts">
import RegionsStatisticsSection from './RegionsStatisticsSection.vue'
import VehicleCount from './VehicleCount.vue'
import MatchmakingList from './MatchmakingList.vue'
import ElectricityGridOwners from './ElectricityGridOwners.vue'
import { County, MapPreset, Municipality, RegionType } from '~/types/types'
import { useStationDataStore } from '~/stores/stations'

const props = defineProps<{
  regionType?: RegionType
  regionId?: string
  regionObject?: County | Municipality
}>()
const { find } = useStrapi()

const { data: mapPresets } = await useAsyncData(
  'map-presets',
  () => find<MapPreset>('map-presets', { populate: '*' }),
  { transform: (_mapPresets) => _mapPresets.data }
)
const getMapPresetSlug = computed(() => (identifier: string) => {
  const preset = mapPresets.value?.find((preset) => preset.attributes.identifier === identifier)
  return preset && `/kartor-och-statistik/${preset.attributes.slug}`
})

const stationStore = useStationDataStore()
const electricityOrigin = computed(() => stationStore.getElectrictyOrigin)
const stationsGroupedByFuelTypeArray = computed(() => {
  const regionType = props.regionType // Get the regionType from props
  const regionId = props.regionId // Get the regionId from props
  const data = stationStore.getExistingStationCountGropedByFuelTypeByRegion(regionType, regionId)

  return data.map((item) => ({
    label: item.fuelType.title,
    value: item.stationCount,
  }))
})
</script>
<template>
  <RegionsStatisticsSection
    title="Ladd- och tankstationer"
    description="Totalt antal stationer per drivmedel"
    :data-destination="getMapPresetSlug('infrastructure-overall')"
  >
    <RegionStatisticsFuelTypeOverview :region-type="regionType" :region-id="regionId" />
  </RegionsStatisticsSection>
  <ChartBar :data="stationsGroupedByFuelTypeArray" direction="vertical" />
  <DataSource
    :source-data="[
      { identifier: electricityOrigin },
      { identifier: 'tankaGront' },
      { identifier: 'energimyndigheten' },
      { identifier: 'trafikverket' },
      { identifier: 'klimatklivet' },
      { identifier: 'biofuelExpress' },
      { identifier: 'energigasSverige' },
    ]"
  />
  <RegionsStatisticsSection
    title="Fordonsflotta"
    description="Visar antalet fordon per drivmedel och fordonstyp"
    :data-destination="getMapPresetSlug('vehicle-fleet')"
  >
    <VehicleCount :region-type="regionType" :region-id="regionId" />
  </RegionsStatisticsSection>

  <RegionsStatisticsSection
    title="Etableringsbehov"
    description="Nedan listas framtida behov av infrastruktur för att klara omställningen till fossilfritt."
    :data-destination="getMapPresetSlug('infrastructure-opportunities')"
  >
    <MatchmakingList :county-id="regionObject?.county_code" />
  </RegionsStatisticsSection>

  <RegionsStatisticsSection
    title="Elnätsägare"
    description="Lista på aktiva nätbolag i området"
    :source-data="[{ identifier: 'energimarknadsinspektionen' }]"
    :data-destination="getMapPresetSlug('electricity-grid-owners')"
  >
    <ElectricityGridOwners :region-type="regionType" :region-id="regionId" />
  </RegionsStatisticsSection>
</template>
